/**
 * DriveZone — Account Page
 * Route: /account  (protected)
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  User as UserIcon, Crown, LogOut, AlertCircle, CheckCircle,
  Clock, Gift, Copy, Check, Loader2, RefreshCw, MessageCircle,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { useAuthStore } from '@/stores/authStore'
import { useSearchStore } from '@/stores/searchStore'

interface StatusResponse {
  subscription_status: string
  is_subscribed:       boolean
  period_end:          string | null
  mpesa_receipt:       string | null
}

interface ReferralStats {
  referral_code:            string
  total_referrals:          number
  successful_referrals:     number
  referral_searches_earned: number
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

const WHATSAPP = '+254748272022'

export default function AccountPage() {
  const navigate         = useNavigate()
  const { user, logout } = useAuthStore()
  const [loggingOut, setLoggingOut]     = useState(false)
  const [renewLoading, setRenewLoading] = useState(false)
  const [copied, setCopied]             = useState(false)

  const { data: statusData } = useQuery<StatusResponse>({
    queryKey: ['subscription-status'],
    queryFn:  async () => (await api.get<StatusResponse>('/api/payments/status/')).data,
    refetchInterval: 30_000,
  })

  const { data: referralData } = useQuery<ReferralStats>({
    queryKey: ['referral-stats'],
    queryFn:  async () => (await api.get<ReferralStats>('/api/referral/stats/')).data,
  })

  function copyReferralLink() {
    const code = referralData?.referral_code ?? user?.referral_code ?? ''
    navigator.clipboard.writeText(`${window.location.origin}/register?ref=${code}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000) })
  }

  async function handleLogout() {
    setLoggingOut(true)
    try { await api.post('/api/auth/logout/') } finally {
      useSearchStore.getState().clearAll()
      logout()
      navigate('/login', { replace: true })
    }
  }

  async function handleRenew() {
    setRenewLoading(true)
    navigate('/subscribe')
  }

  const isSubscribed  = statusData?.is_subscribed ?? user?.is_subscribed ?? false
  const subStatus     = statusData?.subscription_status ?? user?.subscription_status ?? 'free'

  // Badge: if API says is_subscribed but status is "free", they're on complimentary access
  const badgeLabel  = isSubscribed && subStatus === 'free' ? 'Pro (Comped)'
                    : isSubscribed                         ? 'Pro'
                    : subStatus === 'past_due'             ? 'Past Due'
                    : subStatus === 'cancelled'            ? 'Cancelled'
                    : 'Free'

  const badgeColour = isSubscribed
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : subStatus === 'past_due'
      ? 'text-red-700 bg-red-50 border-red-200'
      : 'text-slate-600 bg-slate-50 border-slate-200'

  const badgeIcon = isSubscribed
    ? <CheckCircle className="w-3.5 h-3.5" />
    : subStatus === 'past_due'
      ? <AlertCircle className="w-3.5 h-3.5" />
      : null

  // Show renew button 7 days before expiry
  const showRenew = (() => {
    if (!statusData?.period_end) return false
    const daysLeft = Math.ceil((new Date(statusData.period_end).getTime() - Date.now()) / 86400000)
    return daysLeft <= 7 && daysLeft > 0
  })()

  const whatsappRenewMsg = encodeURIComponent(
    `Hi, I'd like to renew my DriveZone Pro subscription.\nMy registered email: ${user?.email ?? ''}`
  )

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8">
      <div className="max-w-3xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/app/search')}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors"
          >
            ← Back
          </button>
          <div className="w-px h-4 bg-slate-300" />
          <div className="flex items-center gap-2">
            <div className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-slate-100">
              <UserIcon className="w-4 h-4 text-slate-500" />
            </div>
            <h1 className="text-xl font-bold text-slate-900">Your Account</h1>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">

          {/* ── Left: Account details ── */}
          <div className="card p-5 flex flex-col">

            <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
              <span className="text-sm text-slate-500">Email</span>
              <span className="text-sm font-medium text-slate-800 truncate max-w-48">{user?.email}</span>
            </div>

            <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
              <span className="text-sm text-slate-500">Subscription</span>
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full
                                text-xs font-semibold border ${badgeColour}`}>
                {badgeIcon}{badgeLabel}
              </span>
            </div>

            {isSubscribed && statusData?.period_end && (
              <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                <span className="text-sm text-slate-500 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" /> Valid until
                </span>
                <span className="text-sm font-medium text-slate-700">{formatDate(statusData.period_end)}</span>
              </div>
            )}

            {isSubscribed && statusData?.mpesa_receipt && (
              <div className="flex items-center justify-between py-2.5 border-b border-slate-100">
                <span className="text-sm text-slate-500">M-Pesa Receipt</span>
                <span className="text-xs font-mono font-semibold text-slate-700">{statusData.mpesa_receipt}</span>
              </div>
            )}

            {/* Actions */}
            <div className="mt-auto pt-4 space-y-2">
              {isSubscribed ? (
                <>
                  {showRenew && (
                    <button onClick={handleRenew} disabled={renewLoading} className="btn-primary w-full text-sm">
                      {renewLoading
                        ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</>
                        : <><RefreshCw className="w-4 h-4" /> Renew subscription</>}
                    </button>
                  )}
                  <a
                    href={`https://wa.me/${WHATSAPP.replace('+', '')}?text=${whatsappRenewMsg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 py-2 rounded-lg border
                               border-slate-200 text-sm text-slate-500 hover:text-slate-700
                               hover:border-slate-300 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4" /> Contact support via WhatsApp
                  </a>
                </>
              ) : (
                <button onClick={() => navigate('/subscribe')} className="btn-primary w-full">
                  <Crown className="w-4 h-4" /> Upgrade to Pro — KES 300/month
                </button>
              )}

              <button onClick={handleLogout} disabled={loggingOut} className="btn-secondary w-full text-slate-500">
                {loggingOut
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing out…</>
                  : <><LogOut className="w-4 h-4" /> Sign out</>}
              </button>
            </div>
          </div>

          {/* ── Right: Refer & Earn ── */}
          <div id="refer" className="card p-5 flex flex-col gap-5">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                <Gift className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Refer &amp; Earn</h2>
                <p className="text-xs text-slate-500">
                  Every friend who registers gets you{' '}
                  <span className="font-medium text-primary-600">one week of free searches</span>
                </p>
              </div>
            </div>

            {referralData && referralData.total_referrals > referralData.successful_referrals && (
              <p className="text-xs text-slate-400">
                {referralData.total_referrals - referralData.successful_referrals} pending (waiting for account verification)
              </p>
            )}

            {referralData && referralData.referral_searches_earned > 0 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                <p className="text-xs text-emerald-700 font-medium">
                  You've earned {referralData.referral_searches_earned} bonus search{referralData.referral_searches_earned !== 1 ? 'es' : ''} from referrals
                </p>
              </div>
            )}

            <div>
              <p className="text-xs font-medium text-slate-500 mb-1.5">Your referral link</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2
                                text-xs text-slate-600 font-mono truncate">
                  {window.location.origin}/register?ref={referralData?.referral_code ?? user?.referral_code ?? '…'}
                </div>
                <button
                  onClick={copyReferralLink}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg
                             bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copied!</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                </button>
              </div>
            </div>

            <p className="text-xs text-slate-400 mt-auto">
              Your friend must complete registration for the reward to activate.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
